import React, { useEffect, useMemo, useRef, useState } from "react"
import { Video, Sparkles, Mic } from "lucide-react"
import type { CalendarEvent } from "../models/calendar.model"
import { getEventPalette } from "../utils/colorPalette"
import { layoutOverlappingEvents, layoutToCss } from "../utils/layoutEvents"
import { MIN_EVENT_MINUTES, MIN_EVENT_PX, PX_PER_HOUR } from "../utils/timeGrid"
import { useEventPopover } from "../hooks/useEventPopover"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"

/** Type icon — distinguishes meeting/webinar at the same time slot without
 *  altering the status-based palette. */
const TYPE_ICON: Record<CalendarEvent["type"], React.ReactNode> = {
  meeting: <Video className="w-2.5 h-2.5 shrink-0" />,
  webinar: <Sparkles className="w-2.5 h-2.5 shrink-0" />,
  call: <Mic className="w-2.5 h-2.5 shrink-0" />,
}

const HOURS = Array.from({ length: 24 }, (_, i) => i)

function timeToMinutes(t: string) {
  const [h, m] = t.split(":").map(Number)
  return h * 60 + m
}

function sameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

function getWeekDays(date: Date): Date[] {
  const d = new Date(date)
  const day = d.getDay()
  const start = new Date(d)
  start.setDate(d.getDate() - day)
  return Array.from({ length: 7 }, (_, i) => {
    const dd = new Date(start)
    dd.setDate(start.getDate() + i)
    return dd
  })
}

/** A block drawn in one day column. A meeting that runs past midnight is
 *  drawn as two blocks: its own (start → midnight) on the start day, and a
 *  continuation (midnight → end) on the next day that points back at the
 *  real event via `source`. */
type DaySegment = CalendarEvent & { source?: CalendarEvent }

function crossesMidnight(e: CalendarEvent) {
  return timeToMinutes(e.endTime) < timeToMinutes(e.startTime)
}

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]

const formatHour = (h: number) => {
  if (h === 0) return "12 AM"
  if (h < 12) return `${h} AM`
  if (h === 12) return "12 PM"
  return `${h - 12} PM`
}

interface Props {
  currentDate: Date
  events: CalendarEvent[]
  onNavigateDay?: (date: Date) => void
}

export default function WeekView({ currentDate, events, onNavigateDay }: Props) {
  const t = useTranslation()
  const today = new Date()
  const weekDays = useMemo(() => getWeekDays(currentDate), [currentDate])
  const [nowMinutes, setNowMinutes] = useState(() => today.getHours() * 60 + today.getMinutes())
  const scrollRef = useRef<HTMLDivElement>(null)
  const { activeEventId, openPopover, closePopover, popoverPortal } = useEventPopover()

  /** Per-day column-packed layout for overlapping events. Each day column
   *  runs the Google-Calendar-style packer over its own slice, so a busy
   *  Wednesday won't squash an empty Tuesday. Memoised against `events`
   *  and the week boundary. */
  const laidOutByDay = useMemo(() => {
    const map = new Map<string, ReturnType<typeof layoutOverlappingEvents<DaySegment>>>()
    for (const day of weekDays) {
      const prevDay = new Date(day)
      prevDay.setDate(day.getDate() - 1)
      const continuations: DaySegment[] = events
        .filter((e) => sameDay(e.date, prevDay) && crossesMidnight(e) && e.endTime !== "00:00")
        .map((e) => ({ ...e, id: `${e.id}:cont`, startTime: "00:00", source: e }))
      const dayEvents: DaySegment[] = [
        ...continuations,
        ...events.filter((e) => sameDay(e.date, day)),
      ]
      map.set(
        day.toDateString(),
        layoutOverlappingEvents(dayEvents, { minDurationMin: MIN_EVENT_MINUTES }),
      )
    }
    return map
  }, [events, weekDays])

  useEffect(() => {
    const id = setInterval(() => {
      const n = new Date()
      setNowMinutes(n.getHours() * 60 + n.getMinutes())
    }, 60_000)
    return () => clearInterval(id)
  }, [])

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 7 * PX_PER_HOUR
    }
  }, [])

  const todayColIndex = weekDays.findIndex(d => sameDay(d, today))

  return (
    <div className="flex flex-col h-full" onClick={closePopover}>
      {/* Column headers. Both this row and the scroller below reserve the
          same scrollbar gutter, so the grid's vertical scrollbar can't
          narrow the day columns out of line with their headers. */}
      <div className="shrink-0 flex border-b border-border overflow-hidden [scrollbar-gutter:stable]">
        {/* Gutter */}
        <div className="w-16 shrink-0 border-r border-border" />
        {weekDays.map((day, i) => {
          const isToday = sameDay(day, today)
          const dayEvents = events.filter(e => sameDay(e.date, day))
          const hasManyEvents = dayEvents.length > 2

          return (
            <div key={i} className="flex-1 flex flex-col items-center py-2 border-r border-border last:border-r-0 relative">
              {hasManyEvents && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onNavigateDay?.(day)
                  }}
                  className="absolute top-2 right-2 flex items-center justify-center w-5 h-5 rounded-full bg-primary/10 text-primary text-[10px] font-bold hover:bg-primary/20 transition-colors"
                  title={t('calendar.viewAllEvents').replace('{count}', String(dayEvents.length))}
                >
                  +{dayEvents.length - 2}
                </button>
              )}
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                {DAY_LABELS[day.getDay()]}
              </span>
              <span
                className={`mt-0.5 w-8 h-8 flex items-center justify-center rounded-full text-sm font-medium transition-colors ${isToday
                    ? "bg-primary text-white"
                    : "text-foreground"
                  }`}
              >
                {day.getDate()}
              </span>
            </div>
          )
        })}
      </div>

      {/* Scrollable time grid */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto [scrollbar-gutter:stable]">
        <div className="flex relative" style={{ minHeight: `${24 * PX_PER_HOUR}px` }}>
          {/* Hour gutter */}
          <div className="w-16 shrink-0 border-r border-border relative select-none">
            {HOURS.map(h => (
              <div key={h} style={{ height: PX_PER_HOUR }} className="flex items-start pt-1 justify-end pr-2">
                {h > 0 && (
                  <span className="text-[10px] text-muted-foreground -translate-y-2">
                    {formatHour(h)}
                  </span>
                )}
              </div>
            ))}
          </div>

          {/* Day columns */}
          {weekDays.map((day, colIdx) => {
            const laidOutEvents = laidOutByDay.get(day.toDateString()) ?? []
            const isToday = sameDay(day, today)

            return (
              <div
                key={colIdx}
                className="flex-1 relative border-r border-border last:border-r-0"
              >
                {/* Hour lines */}
                {HOURS.map(h => (
                  <div
                    key={h}
                    style={{ top: h * PX_PER_HOUR }}
                    className="absolute left-0 right-0 border-t border-border"
                  />
                ))}
                {/* Half-hour lines */}
                {HOURS.map(h => (
                  <div
                    key={`hf-${h}`}
                    style={{ top: h * PX_PER_HOUR + PX_PER_HOUR / 2 }}
                    className="absolute left-0 right-0 border-t border-dashed border-border/30"
                  />
                ))}

                {/* Today column highlight */}
                {isToday && (
                  <div className="absolute inset-0 bg-primary/[0.02] pointer-events-none" />
                )}

                {/* Current time line (only on today's column) */}
                {isToday && colIdx === todayColIndex && (
                  <div
                    style={{ top: nowMinutes * (PX_PER_HOUR / 60) }}
                    className="absolute left-0 right-0 z-20 pointer-events-none flex items-center"
                  >
                    <div className="w-2.5 h-2.5 rounded-full bg-red-500 -translate-x-1 shrink-0" />
                    <div className="flex-1 border-t border-red-500" />
                  </div>
                )}

                {/* Events — column-packed (Google-Calendar-style overlap
                    layout) using the existing soft palette + status-coloured
                    left border. Type icon distinguishes meeting/webinar at
                    the same time slot. */}
                {laidOutEvents.map(({ event: ev, layout }) => {
                  const startMin = timeToMinutes(ev.startTime)
                  const rawEndMin = timeToMinutes(ev.endTime)
                  // A meeting running past midnight ends "earlier" than it
                  // starts; draw it to the bottom of its start day.
                  const endMin = rawEndMin < startMin ? 24 * 60 : rawEndMin
                  const top = startMin * (PX_PER_HOUR / 60)
                  const height = Math.max((endMin - startMin) * (PX_PER_HOUR / 60), MIN_EVENT_PX)
                  const palette = getEventPalette(ev)
                  // Both halves of a past-midnight meeting open, and light
                  // up for, the one real event.
                  const sourceEvent = ev.source ?? ev
                  const isActive = activeEventId === sourceEvent.id
                  const isDarkMode = document.documentElement.classList.contains('dark')
                  const { left, width } = layoutToCss(layout, { outerInsetPx: 4, gapPx: 3 })

                  return (
                    <div
                      key={ev.id}
                      onClick={e => {
                        e.stopPropagation()
                        if (isActive) {
                          closePopover()
                        } else {
                          openPopover(sourceEvent, e.currentTarget as HTMLElement)
                        }
                      }}
                      style={{
                        top,
                        height,
                        left,
                        width,
                        background: isDarkMode ? palette.darkLight : palette.light,
                        borderLeft: `3px solid ${palette.solid}`,
                        color: isDarkMode ? palette.darkText : palette.text,
                      }}
                      className={`absolute rounded-md px-1.5 py-0.5 cursor-pointer transition-all overflow-hidden z-10 ${isActive ? "ring-2 ring-offset-1 ring-primary/50" : ""
                        }`}
                    >
                      <div className="flex items-center gap-1 min-w-0">
                        <span className="opacity-70 shrink-0">{TYPE_ICON[ev.type]}</span>
                        <p className="text-[10px] font-semibold leading-tight truncate">{ev.title}</p>
                      </div>
                      {height > 28 && (
                        <p className="text-[9px] leading-tight truncate opacity-80 pl-3.5">
                          {sourceEvent.startTime}
                        </p>
                      )}
                    </div>
                  )
                })}
              </div>
            )
          })}
        </div>
      </div>
      {popoverPortal}
    </div>
  )
}
